(() => {
  if (customElements.get("h2o-product-description")) return;

  class H2OProductDescription extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;

      this.viewport = this.querySelector(".h2o-product-description__viewport");
      this.preview = this.querySelector("[data-description-preview]");
      this.full = this.querySelector("[data-description-full]");
      this.toggle = this.querySelector("[data-description-toggle]");
      this.moreLabel = this.querySelector("[data-description-more]");
      this.lessLabel = this.querySelector("[data-description-less]");

      if (!this.viewport || !this.preview || !this.full || !this.toggle) return;

      this.dataset.initialized = "true";
      this.onToggle = this.onToggle.bind(this);
      this.toggle.addEventListener("click", this.onToggle);
      this.prepare();
    }

    disconnectedCallback() {
      if (this.toggle && this.onToggle) {
        this.toggle.removeEventListener("click", this.onToggle);
      }
      window.clearTimeout(this.transitionTimer);
      delete this.dataset.initialized;
    }

    prepare() {
      const fullHeight = this.measureHeight(this.full, true);
      const previewHeight = this.measureHeight(this.preview, false);
      const lineHeight = Number.parseFloat(getComputedStyle(this.preview).lineHeight) || 20;
      const needsDisclosure = this.preview.scrollHeight > previewHeight + 1 || fullHeight > previewHeight + lineHeight * 0.5;

      if (needsDisclosure && !this.preview.textContent.trim().endsWith("…")) {
        this.preview.textContent = this.preview.textContent.trim() + "…";
      }

      if (!needsDisclosure) {
        this.preview.hidden = true;
        this.full.hidden = false;
        this.full.removeAttribute("aria-hidden");
        this.toggle.hidden = true;
        return;
      }

      this.toggle.hidden = false;
      const defaultExpanded = this.dataset.defaultExpanded === "true";
      this.setState(defaultExpanded);
    }

    measureHeight(element, useScrollHeight) {
      const wasHidden = element.hidden;
      const previous = {
        position: element.style.position,
        visibility: element.style.visibility,
        pointerEvents: element.style.pointerEvents
      };

      if (wasHidden) {
        element.hidden = false;
        element.style.position = "absolute";
        element.style.visibility = "hidden";
        element.style.pointerEvents = "none";
      }

      const height = useScrollHeight ? element.scrollHeight : element.getBoundingClientRect().height;

      if (wasHidden) {
        element.hidden = true;
        element.style.position = previous.position;
        element.style.visibility = previous.visibility;
        element.style.pointerEvents = previous.pointerEvents;
      }

      return height;
    }

    setState(expanded) {
      this.expanded = expanded;
      this.toggle.setAttribute("aria-expanded", String(expanded));
      this.toggle.setAttribute(
        "aria-label",
        expanded ? this.toggle.dataset.collapseLabel : this.toggle.dataset.expandLabel
      );
      this.moreLabel.hidden = expanded;
      this.lessLabel.hidden = !expanded;
      this.classList.toggle("is-expanded", expanded);
      this.classList.toggle("is-collapsed", !expanded);

      this.preview.hidden = expanded;
      this.preview.setAttribute("aria-hidden", String(expanded));
      this.full.hidden = !expanded;
      this.full.setAttribute("aria-hidden", String(!expanded));
    }

    onToggle() {
      if (this.animating) return;

      const nextExpanded = !this.expanded;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (reduceMotion) {
        this.setState(nextExpanded);
        return;
      }

      this.animateTo(nextExpanded);
    }

    animateTo(expanded) {
      this.animating = true;
      const startHeight = this.viewport.getBoundingClientRect().height;

      this.viewport.style.height = `${startHeight}px`;
      this.classList.add("is-transitioning");

      if (expanded) {
        this.full.hidden = false;
        this.full.removeAttribute("aria-hidden");
        this.full.style.opacity = "0";
        this.preview.setAttribute("aria-hidden", "true");
        this.placePreviewOverContent("1");
      } else {
        this.preview.hidden = false;
        this.preview.removeAttribute("aria-hidden");
        this.placePreviewOverContent("0");
        this.full.setAttribute("aria-hidden", "true");
      }

      const targetHeight = expanded ? this.full.scrollHeight : this.preview.getBoundingClientRect().height;
      this.updateControl(expanded);
      this.viewport.getBoundingClientRect();

      requestAnimationFrame(() => {
        this.viewport.style.height = `${targetHeight}px`;
        this.preview.style.opacity = expanded ? "0" : "1";
        this.full.style.opacity = expanded ? "1" : "0";
      });

      const duration = Number.parseInt(getComputedStyle(this).getPropertyValue("--h2o-description-duration"), 10) || 250;
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = window.setTimeout(() => this.finishTransition(expanded), duration + 80);
    }

    placePreviewOverContent(opacity) {
      this.preview.style.position = "absolute";
      this.preview.style.top = "0";
      this.preview.style.right = "0";
      this.preview.style.left = "0";
      this.preview.style.opacity = opacity;
      this.preview.style.pointerEvents = "none";
    }

    updateControl(expanded) {
      this.expanded = expanded;
      this.toggle.setAttribute("aria-expanded", String(expanded));
      this.toggle.setAttribute(
        "aria-label",
        expanded ? this.toggle.dataset.collapseLabel : this.toggle.dataset.expandLabel
      );
      this.moreLabel.hidden = expanded;
      this.lessLabel.hidden = !expanded;
      this.classList.toggle("is-expanded", expanded);
      this.classList.toggle("is-collapsed", !expanded);
    }

    finishTransition(expanded) {
      this.preview.hidden = expanded;
      this.preview.setAttribute("aria-hidden", String(expanded));
      this.preview.style.position = "";
      this.preview.style.top = "";
      this.preview.style.right = "";
      this.preview.style.left = "";
      this.preview.style.opacity = "";
      this.preview.style.pointerEvents = "";

      this.full.hidden = !expanded;
      this.full.setAttribute("aria-hidden", String(!expanded));
      this.full.style.opacity = "";

      this.viewport.style.height = "";
      this.classList.remove("is-transitioning");
      this.animating = false;
    }
  }

  customElements.define("h2o-product-description", H2OProductDescription);
})();
